import { useEffect, useState } from "react";
import { Save, Send, CheckCircle2, ArrowLeft, RotateCcw, FileDown, Link2, ShieldCheck, XCircle, Ban, Mail } from "lucide-react";
import { SendDialog } from "./SendDialog";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Quote } from "@/lib/types";
import { api, errMsg, BACKEND_URL } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

interface Props { quote: Quote; dirty: boolean; saving: boolean; onSave: () => Promise<void>; onAction: (action: string, note?: string, extra?: any) => Promise<void>; editable: boolean }

export function StatusBar({ quote, dirty, saving, onSave, onAction, editable }: Props) {
  const nav = useNavigate();
  const { user } = useAuth() as any;
  const admin = user?.role === "admin";
  const s = quote.status;
  const shareUrl = quote.share_token ? `${window.location.origin}/q/${quote.share_token}` : null;
  const [sendMode, setSendMode] = useState<"send" | "resend" | null>(null);

  useEffect(() => {
    const h = (e: KeyboardEvent) => { if ((e.metaKey || e.ctrlKey) && e.key === "s") { e.preventDefault(); if (editable) onSave(); } };
    window.addEventListener("keydown", h); return () => window.removeEventListener("keydown", h);
  }, [onSave, editable]);

  const downloadPdf = async () => {
    const t = toast.loading("Rendering PDF…");
    try {
      const r = await api.get(`/quotes/${quote.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data); window.open(url, "_blank"); toast.dismiss(t);
    } catch (e) { toast.dismiss(t); toast.error(errMsg(e)); }
  };

  const copyLink = () => { if (shareUrl) { navigator.clipboard.writeText(shareUrl); toast.success("Share link copied"); } };
  const resend = async (channels: string[]) => {
    const t = toast.loading("Delivering proposal link…");
    try {
      const { data } = await api.post(`/quotes/${quote.id}/deliver`, { share_url: `${window.location.origin}/q/{token}`, channels });
      toast.dismiss(t); reportDelivery(data);
    } catch (e) { toast.dismiss(t); toast.error(errMsg(e)); }
  };

  const hint: Record<string, string> = {
    draft: editable ? "Editing enabled. Submit for review or approve when ready." : "",
    internal_review: admin ? "Awaiting your approval." : "Waiting for an admin to approve.",
    approved: "Locked. Send to homeowner, or create a revision to edit.",
    sent: "Sent — waiting for the homeowner to open it.",
    viewed: "Homeowner has viewed the proposal.",
    accepted: `Accepted by ${quote.acceptance?.signature_name || "homeowner"}.`,
    rejected: "Homeowner declined. Create a revision to re-quote.",
    revision_requested: `Changes requested: “${quote.revision_request?.message || ""}”`,
  };

  return (
    <div className="sticky top-0 z-30 border-b border-gray-200 bg-white/90 backdrop-blur" data-testid="status-bar">
      <div className="flex flex-wrap items-center gap-2 px-3 py-2 sm:gap-3 sm:px-4 sm:py-2.5">
        <button data-testid="back-to-dashboard-btn" onClick={() => nav("/quotes")} className="rounded-md p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-900"><ArrowLeft size={18} /></button>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="truncate text-sm font-semibold text-gray-900" data-testid="quote-title">{quote.title}</span>
            <StatusBadge status={s} />
            <span className="rounded-md bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600" data-testid="quote-version">v{quote.current_version}</span>
            {dirty && <span className="text-xs text-amber-600" data-testid="unsaved-indicator">• Unsaved changes</span>}
          </div>
          <div className="truncate text-xs text-gray-500" data-testid="status-hint">{hint[s]}</div>
        </div>
        <div className="flex w-full flex-wrap items-center gap-1.5 sm:w-auto">
          {editable && <Button size="sm" variant="outline" data-testid="save-quote-btn" onClick={onSave} disabled={saving || !dirty}><Save size={14} className="mr-1.5" />{saving ? "Saving…" : "Save"}</Button>}
          <Button size="sm" variant="outline" data-testid="download-pdf-btn" onClick={downloadPdf}><FileDown size={14} className="mr-1.5" />PDF</Button>
          {shareUrl && <Button size="sm" variant="outline" data-testid="copy-share-link-btn" onClick={copyLink}><Link2 size={14} className="mr-1.5" />Copy link</Button>}
          {shareUrl && ["sent", "viewed", "revision_requested"].includes(s) && <Button size="sm" variant="outline" data-testid="resend-proposal-btn" onClick={() => setSendMode("resend")}><Mail size={14} className="mr-1.5" />Resend link</Button>}
          {s === "draft" && !admin && <Button size="sm" data-testid="submit-review-btn" onClick={() => onAction("submit_review")} className="bg-blue-700 hover:bg-blue-800"><ShieldCheck size={14} className="mr-1.5" />Submit for approval</Button>}
          {s === "draft" && admin && <Button size="sm" variant="outline" data-testid="submit-review-btn" onClick={() => onAction("submit_review")}><ShieldCheck size={14} className="mr-1.5" />Send to review</Button>}
          {["draft", "internal_review"].includes(s) && admin && <Button size="sm" data-testid="approve-quote-btn" onClick={() => onAction("approve")} className="bg-emerald-600 hover:bg-emerald-700"><CheckCircle2 size={14} className="mr-1.5" />Approve</Button>}
          {s === "internal_review" && admin && <Button size="sm" variant="outline" data-testid="reject-review-btn" onClick={() => { const n = window.prompt("Reason to send back to draft?") || ""; onAction("reject_review", n); }}><XCircle size={14} className="mr-1.5" />Send back</Button>}
          {s === "approved" && <Button size="sm" data-testid="send-quote-btn" onClick={() => setSendMode("send")} className="bg-blue-700 hover:bg-blue-800"><Send size={14} className="mr-1.5" />Send to homeowner</Button>}
          {["sent", "viewed"].includes(s) && <Button size="sm" variant="outline" data-testid="mark-rejected-btn" onClick={() => onAction("mark_rejected")}><Ban size={14} className="mr-1.5" />Mark declined</Button>}
          {!["draft"].includes(s) && <Button size="sm" variant="outline" data-testid="create-revision-btn" onClick={() => onAction("revise")}><RotateCcw size={14} className="mr-1.5" />New revision</Button>}
        </div>
      </div>
      {["approved", "sent", "viewed", "accepted", "rejected", "revision_requested"].includes(s) && (
        <div className={`px-4 py-1.5 text-xs font-medium ${s === "accepted" ? "bg-green-50 text-green-800" : s === "revision_requested" ? "bg-orange-50 text-orange-800" : "bg-blue-50 text-blue-800"}`} data-testid="locked-banner">
          Version {quote.current_version} is locked{quote.approval ? ` — approved by ${quote.approval.approved_by}` : ""}. Create a new revision to make changes; previous versions are preserved in History.
        </div>
      )}
      {sendMode && <SendDialog open quote={quote} mode={sendMode} onClose={() => setSendMode(null)}
        onConfirm={async (channels) => { if (sendMode === "send") await onAction("send", "", { channels }); else await resend(channels); }} />}
    </div>
  );
}

export function reportDelivery(delivery: any) {
  if (!delivery) return;
  const ok = Object.entries(delivery).filter(([, r]: any) => r.sent).map(([k, r]: any) => `${k === "sms" ? "texted" : "emailed"} ${r.to}`);
  const bad = Object.entries(delivery).filter(([, r]: any) => !r.sent).map(([k, r]: any) => `${k.toUpperCase()}: ${r.reason}`);
  if (ok.length) toast.success(`Proposal ${ok.join(" and ")}`);
  bad.forEach((b) => toast.warning(b));
}
